/**
 * Base API client utility
 */

import { API_BASE_URL, DEFAULT_HEADERS, DEBUG } from './config';
import { logger } from './logger';

/**
 * Función auxiliar para realizar llamadas a la API
 * @param {string} endpoint - Endpoint relativo (sin el API_BASE_URL)
 * @param {Object} body - Cuerpo de la petición
 * @param {string} method - Método HTTP (default: 'POST')
 * @param {Object} headers - Headers adicionales para sobrescribir los por defecto
 * @param {Object} options - Opciones adicionales para fetch
 * @returns {Promise<Object>} - Respuesta de la API
 */
export async function callApi(endpoint, body, method = 'POST', headers = {}, options = {}) {
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Mostrar información detallada para depuración en endpoints específicos
    if (endpoint === '/tasks/update' && DEBUG) {
      // Los logs de debug están desactivados
    }
    
    // Configurar opciones de fetch
    const fetchOptions = {
      method,
      headers: { ...DEFAULT_HEADERS, ...headers },
      ...options
    };
    
    // Solo incluir body para métodos que lo requieren (no GET, HEAD, etc.)
    if (method !== 'GET' && method !== 'HEAD' && body !== null && body !== undefined) {
      fetchOptions.body = JSON.stringify(body);
    }
    
    const response = await fetch(url, fetchOptions);
    
    // Check if the response is JSON
    const contentType = response.headers.get('content-type');
    
    // For non-JSON responses or empty responses, return the response directly
    if (!contentType || !contentType.includes('application/json')) {
      // Check if the status is OK
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Return a simplified response object for non-JSON responses
      return {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        body: await response.text()
      };
    }
    
    // Parse JSON response
    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      // If JSON parsing fails, try to get text response
      const textResponse = await response.text();
      logger.error(`Failed to parse JSON response for ${endpoint}:`, textResponse);
      if (!response.ok) {
        const apiError = new Error(`Error en llamada a API: ${endpoint} (${response.status})`);
        apiError.status = response.status;
        apiError.responseText = textResponse;
        throw apiError;
      }
      return { body: textResponse, status: response.status };
    }
    
    // Log response details for debugging specific endpoints
    if (endpoint === '/tasks/update' && DEBUG) {
      // Los logs de debug están desactivados
    }
    
    if (!response.ok) {
      // Log full error response for debugging
      logger.error(`API Error Response for ${endpoint}:`, {
        status: response.status,
        statusText: response.statusText,
        data: data
      });
      
      const errorMessage = data.message || data.error || data.body?.message || data.body?.error || `Error en llamada a API: ${endpoint}`;
      const apiError = new Error(errorMessage);
      // Store status code and full response for error handling
      apiError.status = response.status;
      apiError.responseData = data;
      throw apiError;
    }
    
    return data;
  } catch (error) {
    // Don't log 404 errors for /permisos/update as they're expected and handled gracefully
    // (user doesn't exist, will be created automatically)
    if (error.status === 404 && endpoint === '/permisos/update') {
      // This is expected - user doesn't exist, will be handled by editUserPermissions
      // Don't log as error, just re-throw
      throw error;
    }
    
    logger.error(`Error en llamada a API ${endpoint}:`, error);
    throw error;
  }
}

/**
 * Simple cache for API responses
 */
export const apiCache = {
  tasks: {},  // Store tasks by workspace
  workspaces: {},  // Store workspaces by department code and admin flag
  pendingRequests: {},  // Track in-flight requests
  permissions: null  // Store user permissions
};

/**
 * Counter for tracking API calls
 */
export const apiCallCounter = {
  fetchTasks: 0,
  sendTask: 0,
  updateTask: 0,
  deleteTask: 0,
  fetchWorkspaces: 0,
  createWorkspace: 0,
  deleteWorkspace: 0,
  fetchPermissions: 0
};